import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Globe,
  Network,
  Plug,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Search,
  Settings,
  Server,
  Smartphone,
  Cloud,
  Users,
  MoreVertical,
  Eye,
  Calendar,
  Target as TargetIcon,
  Layers,
  Copy,
  Monitor
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useLoginContext } from '../context/LoginContext';

// Styled Components
const Container = styled.div`
  padding: 1rem;
  max-width: 1600px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 1rem;
  }
  @media (min-width: 1280px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const ScanCount = styled.span`
  font-size: 0.875rem;
  color: #00419F;
  font-weight: 500;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 1rem 0.625rem 2.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  font-size: 0.875rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #1562B7;
    box-shadow: 0 0 0 1px #1562B7;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  width: 1rem;
  height: 1rem;
`;

const AdvancedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const AdvancedCard = styled.div`
  background: white;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const AdvancedCardHeader = styled.div`
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const AdvancedHeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
`;

const AdvancedIconWrapper = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  flex-shrink: 0;
`;

const AdvancedCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const AdvancedScanTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.25rem;
`;

const AdvancedScanSubtitle = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
`;

const AdvancedHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  background: ${props => props.bgColor || '#f3f4f6'};
  color: ${props => props.textColor || '#374151'};
`;

const MoreButton = styled.button`
  padding: 0.25rem;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const AdvancedCardBody = styled.div`
  padding: 0 1.5rem 1.5rem;
`;

const AdvancedInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AdvancedInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
`;

const AdvancedInfoLabel = styled.span`
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.025em;
`;

const AdvancedInfoValue = styled.div`
  font-weight: 500;
  color: #0f172a;
  margin-top: 0.125rem;
`;

const ScanIdSection = styled.div`
  background: #f8fafc;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
`;

const ScanIdLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const ScanIdWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const ScanIdCode = styled.code`
  font-size: 0.75rem;
  font-family: 'Monaco', 'Menlo', monospace;
  color: #0f172a;
  background: transparent;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CopyButton = styled.button`
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  
  &:hover {
    color: #374151;
    background: #e2e8f0;
  }
`;

const AdvancedCardFooter = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const AdvancedButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  
  ${props => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    border: none;
    
    &:hover {
      background: #2563eb;
    }
  ` : `
    background: white;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #f8fafc;
      color: #374151;
    }
    
    ${props.disabled ? `
      opacity: 0.5;
      cursor: not-allowed;
      
      &:hover {
        background: white;
        color: #64748b;
      }
    ` : ''}
  `}
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 2px solid #E5E7EB;
  border-top-color: #1562B7;
  animation: ${spin} 1s linear infinite;

  @media (max-width: 640px) {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const ErrorWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #dc2626;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #64748b;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #94a3b8;
`;

// Scan type configurations
const scanTypeConfigs = {
  'web': {
    id: 'web',
    name: 'Web Application Scan',
    icon: Globe,
    description: 'Comprehensive OWASP Top 10 security assessment with manual verification',
    features: ['XSS Detection', 'SQL Injection', 'CSRF Protection', 'Authentication Bypass'],
    complexity: 'Intermediate',
    premium: false,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(59, 130, 246, 0.2)',
      hoverBorderColor: 'rgba(59, 130, 246, 0.4)',
      accentColor: '#3b82f6',
      iconBg: 'rgba(59, 130, 246, 0.1)',
      iconBorder: 'rgba(59, 130, 246, 0.2)',
      iconColor: '#2563eb',
      buttonColor: '#2563eb',
      buttonHoverBg: 'rgba(59, 130, 246, 0.1)',
      buttonHoverColor: '#1d4ed8',
      featureDot: '#3b82f6'
    }
  },
  'network': {
    id: 'network',
    name: 'Network Infrastructure',
    icon: Network,
    description: 'Deep network topology analysis with manual penetration testing',
    features: ['Port Scanning', 'Service Detection', 'Network Mapping', 'Vulnerability Assessment'],
    complexity: 'Intermediate',
    premium: false,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(168, 85, 247, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(168, 85, 247, 0.2)',
      hoverBorderColor: 'rgba(168, 85, 247, 0.4)',
      accentColor: '#a855f7',
      iconBg: 'rgba(168, 85, 247, 0.1)',
      iconBorder: 'rgba(168, 85, 247, 0.2)',
      iconColor: '#9333ea',
      buttonColor: '#9333ea',
      buttonHoverBg: 'rgba(168, 85, 247, 0.1)',
      buttonHoverColor: '#7c3aed',
      featureDot: '#a855f7'
    }
  },
  'api': {
    id: 'api',
    name: 'API Security Testing',
    icon: Server,
    description: 'REST/GraphQL API comprehensive security audit with manual testing',
    features: ['Endpoint Security', 'Authentication Testing', 'Data Validation', 'Rate Limiting'],
    complexity: 'Intermediate',
    premium: false,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(16, 185, 129, 0.2)',
      hoverBorderColor: 'rgba(16, 185, 129, 0.4)',
      accentColor: '#10b981',
      iconBg: 'rgba(16, 185, 129, 0.1)',
      iconBorder: 'rgba(16, 185, 129, 0.2)',
      iconColor: '#059669',
      buttonColor: '#059669',
      buttonHoverBg: 'rgba(16, 185, 129, 0.1)',
      buttonHoverColor: '#047857',
      featureDot: '#10b981'
    }
  },
  'firewall': {
    id: 'firewall',
    name: 'Firewall Assessment',
    icon: Shield,
    description: 'Advanced firewall rules and policy analysis with bypass testing',
    features: ['Rule Analysis', 'Policy Verification', 'Security Gaps', 'Compliance Check'],
    complexity: 'Advanced',
    premium: false,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(239, 68, 68, 0.2)',
      hoverBorderColor: 'rgba(239, 68, 68, 0.4)',
      accentColor: '#ef4444',
      iconBg: 'rgba(239, 68, 68, 0.1)',
      iconBorder: 'rgba(239, 68, 68, 0.2)',
      iconColor: '#dc2626',
      buttonColor: '#dc2626',
      buttonHoverBg: 'rgba(239, 68, 68, 0.1)',
      buttonHoverColor: '#b91c1c',
      featureDot: '#ef4444'
    }
  },
  'thick-client': {
    id: 'thick-client',
    name: 'Thick Client Pentesting',
    icon: Monitor,
    description: 'Desktop application security assessment with reverse engineering',
    features: ['Binary Analysis', 'Memory Protection', 'Local Storage', 'Communication Security'],
    complexity: 'Advanced',
    premium: true,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(99, 102, 241, 0.2)',
      hoverBorderColor: 'rgba(99, 102, 241, 0.4)',
      accentColor: '#6366f1',
      iconBg: 'rgba(99, 102, 241, 0.1)',
      iconBorder: 'rgba(99, 102, 241, 0.2)',
      iconColor: '#4f46e5',
      buttonColor: '#4f46e5',
      buttonHoverBg: 'rgba(99, 102, 241, 0.1)',
      buttonHoverColor: '#4338ca',
      featureDot: '#6366f1'
    }
  },
  'thin-client': {
    id: 'thin-client',
    name: 'Thin Client Pentesting',
    icon: Globe,
    description: 'Browser-based application security testing with client-side analysis',
    features: ['Client-Side Security', 'Session Management', 'DOM Analysis', 'Browser Exploits'],
    complexity: 'Advanced',
    premium: true,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(6, 182, 212, 0.2)',
      hoverBorderColor: 'rgba(6, 182, 212, 0.4)',
      accentColor: '#06b6d4',
      iconBg: 'rgba(6, 182, 212, 0.1)',
      iconBorder: 'rgba(6, 182, 212, 0.2)',
      iconColor: '#0891b2',
      buttonColor: '#0891b2',
      buttonHoverBg: 'rgba(6, 182, 212, 0.1)',
      buttonHoverColor: '#0e7490',
      featureDot: '#06b6d4'
    }
  },
  'mobile-pt': {
    id: 'mobile-pt',
    name: 'Mobile App Security Testing',
    icon: Smartphone,
    description: 'iOS/Android application security analysis with runtime testing',
    features: ['Static Analysis', 'Dynamic Testing', 'Runtime Protection', 'Data Storage'],
    complexity: 'Expert',
    premium: true,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(236, 72, 153, 0.2)',
      hoverBorderColor: 'rgba(236, 72, 153, 0.4)',
      accentColor: '#ec4899',
      iconBg: 'rgba(236, 72, 153, 0.1)',
      iconBorder: 'rgba(236, 72, 153, 0.2)',
      iconColor: '#db2777',
      buttonColor: '#db2777',
      buttonHoverBg: 'rgba(236, 72, 153, 0.1)',
      buttonHoverColor: '#be185d',
      featureDot: '#ec4899'
    }
  },
  'red-team': {
    id: 'red-team',
    name: 'Red Team Operations',
    icon: Users,
    description: 'Full-scale adversarial simulation exercise with social engineering',
    features: ['Social Engineering', 'Physical Security', 'Advanced Persistence', 'Lateral Movement'],
    complexity: 'Expert',
    premium: true,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(249, 115, 22, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(249, 115, 22, 0.2)',
      hoverBorderColor: 'rgba(249, 115, 22, 0.4)',
      accentColor: '#f97316',
      iconBg: 'rgba(249, 115, 22, 0.1)',
      iconBorder: 'rgba(249, 115, 22, 0.2)',
      iconColor: '#ea580c',
      buttonColor: '#ea580c',
      buttonHoverBg: 'rgba(249, 115, 22, 0.1)',
      buttonHoverColor: '#c2410c',
      featureDot: '#f97316'
    }
  },
  'cloud-pt': {
    id: 'cloud-pt',
    name: 'Cloud Application Pentest',
    icon: Cloud,
    description: 'Cloud-native application security assessment with infrastructure testing',
    features: ['Container Security', 'IAM Analysis', 'Cloud Config', 'Serverless Testing'],
    complexity: 'Expert',
    premium: true,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(139, 92, 246, 0.2)',
      hoverBorderColor: 'rgba(139, 92, 246, 0.4)',
      accentColor: '#8b5cf6',
      iconBg: 'rgba(139, 92, 246, 0.1)',
      iconBorder: 'rgba(139, 92, 246, 0.2)',
      iconColor: '#7c3aed',
      buttonColor: '#7c3aed',
      buttonHoverBg: 'rgba(139, 92, 246, 0.1)',
      buttonHoverColor: '#6d28d9',
      featureDot: '#8b5cf6'
    }
  },
  'default': {
    id: 'default',
    name: 'Security Assessment',
    icon: Shield,
    description: 'General Security Testing',
    features: ['Security Analysis'],
    complexity: 'Standard',
    premium: false,
    colors: {
      bgGradient: 'linear-gradient(135deg, rgba(107, 114, 128, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
      borderColor: 'rgba(107, 114, 128, 0.2)',
      hoverBorderColor: 'rgba(107, 114, 128, 0.4)',
      accentColor: '#6b7280',
      iconBg: 'rgba(107, 114, 128, 0.1)',
      iconBorder: 'rgba(107, 114, 128, 0.2)',
      iconColor: '#4b5563',
      buttonColor: '#4b5563',
      buttonHoverBg: 'rgba(107, 114, 128, 0.1)',
      buttonHoverColor: '#374151',
      featureDot: '#6b7280'
    }
  }
};

// Status configurations
const getStatusConfig = (status) => {
  const configs = {
    'Testing': {
      bgColor: '#dbeafe',
      textColor: '#1e40af',
      label: 'Testing In Progress'
    },
    'Testing In Progress': {
      bgColor: '#dbeafe',
      textColor: '#1e40af',
      label: 'Testing In Progress'
    },
    'Reporting': {
      bgColor: '#fed7aa',
      textColor: '#c2410c',
      label: 'Reporting'
    },
    'Quality Check': {
      bgColor: '#e9d5ff',
      textColor: '#7c2d12',
      label: 'Quality Check'
    },
    'completed': {
      bgColor: '#bbf7d0',
      textColor: '#065f46',
      label: 'Completed'
    }
  };

  return configs[status] || {
    bgColor: '#f3f4f6',
    textColor: '#374151',
    label: status || 'Unknown'
  };
};

const getScanTypeConfig = (scanType) => {
  if (!scanType) return scanTypeConfigs['default'];

  const lowerScanType = scanType.toLowerCase();

  // Direct match first
  if (scanTypeConfigs[lowerScanType]) {
    return scanTypeConfigs[lowerScanType];
  }

  // Partial matches
  if (lowerScanType.includes('web') || lowerScanType.includes('app')) return scanTypeConfigs['web'];
  if (lowerScanType.includes('network') || lowerScanType.includes('infra')) return scanTypeConfigs['network'];
  if (lowerScanType.includes('api')) return scanTypeConfigs['api'];
  if (lowerScanType.includes('firewall')) return scanTypeConfigs['firewall'];
  if (lowerScanType.includes('thick') || lowerScanType.includes('desktop')) return scanTypeConfigs['thick-client'];
  if (lowerScanType.includes('thin') || lowerScanType.includes('browser')) return scanTypeConfigs['thin-client'];
  if (lowerScanType.includes('mobile')) return scanTypeConfigs['mobile-pt'];
  if (lowerScanType.includes('red') || lowerScanType.includes('team')) return scanTypeConfigs['red-team'];
  if (lowerScanType.includes('cloud')) return scanTypeConfigs['cloud-pt'];

  return scanTypeConfigs['default'];
};

function AdvancedReportPage() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useLoginContext();
  const userTimeZone = user?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const fetchAdvancedScans = async () => {
      try {
        const response = await apiRequest('/api/advanced-scans/', { method: 'GET' });
        if (!response.ok) throw new Error('Failed to fetch advanced scans');
        const data = await response.json();
        setScans(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAdvancedScans();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: userTimeZone,
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(dateString));
  };

  const getTargetDisplay = (targetDetails, scanType) => {
    if (!targetDetails) return 'N/A';

    if (targetDetails.urls) return targetDetails.urls[0];
    if (targetDetails.ips) return targetDetails.ips.join(', ');
    if (targetDetails.endpoints) return targetDetails.endpoints[0];

    return 'Multiple targets';
  };

  const filteredScans = scans.filter(scan =>
    (scan.form_id && scan.form_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (scan.status && scan.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (scan.scan_type && scan.scan_type.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Container>
          <LoadingWrapper>
            <LoadingSpinner />
          </LoadingWrapper>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Container>
          <ErrorWrapper>
            <AlertTriangle size={20} style={{ marginRight: '0.5rem' }} />
            Error: {error}
          </ErrorWrapper>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Container>
        {/* <Header>
          <HeaderTop>
            <Title>Advanced Security Assessments</Title>
            <ScanCount>{scans.length} Total Scans</ScanCount>
          </HeaderTop>
          
          <SearchBar>
            <SearchWrapper>
              <SearchIcon />
              <SearchInput
                type="text"
                placeholder="Search by scan ID, status, or scan type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchWrapper>
          </SearchBar>
        </Header> */}

        {filteredScans.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <Settings size={24} />
            </EmptyStateIcon>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              No Advanced Scans Found
            </h3>
            <p>No advanced security assessments match your search criteria.</p>
          </EmptyState>
        ) : (
          <AdvancedGrid>
            {filteredScans.map((scan) => {
              const typeConfig = getScanTypeConfig(scan.scan_type);
              const statusConfig = getStatusConfig(scan.status);
              const TypeIcon = typeConfig.icon;

              return (
                <AdvancedCard key={scan._id}>
                  {/* Card Header */}
                  <AdvancedCardHeader>
                    <AdvancedHeaderLeft>
                      <AdvancedIconWrapper>
                        <TypeIcon size={20} />
                      </AdvancedIconWrapper>
                      <AdvancedCardContent>
                        <AdvancedScanTitle>
                          {typeConfig.name}
                        </AdvancedScanTitle>
                        <AdvancedScanSubtitle>{typeConfig.description}</AdvancedScanSubtitle>
                      </AdvancedCardContent>
                    </AdvancedHeaderLeft>

                    <AdvancedHeaderRight>
                      <StatusBadge
                        bgColor={statusConfig.bgColor}
                        textColor={statusConfig.textColor}
                      >
                        {statusConfig.label}
                      </StatusBadge>
                      <MoreButton>
                        <MoreVertical size={16} />
                      </MoreButton>
                    </AdvancedHeaderRight>
                  </AdvancedCardHeader>

                  {/* Card Body */}
                  <AdvancedCardBody>
                    <AdvancedInfoGrid>
                      <div>
                        <AdvancedInfoItem>
                          <Calendar size={16} />
                          <AdvancedInfoLabel>Created</AdvancedInfoLabel>
                        </AdvancedInfoItem>
                        <AdvancedInfoValue>{formatDate(scan.created_at)}</AdvancedInfoValue>
                      </div>

                      <div>
                        <AdvancedInfoItem>
                          <TargetIcon size={16} />
                          <AdvancedInfoLabel>Target</AdvancedInfoLabel>
                        </AdvancedInfoItem>
                        <AdvancedInfoValue title={getTargetDisplay(scan.target_details, scan.scan_type)}>
                          {getTargetDisplay(scan.target_details, scan.scan_type)}
                        </AdvancedInfoValue>
                      </div>

                      <div>
                        <AdvancedInfoItem>
                          <Clock size={16} />
                          <AdvancedInfoLabel>Started</AdvancedInfoLabel>
                        </AdvancedInfoItem>
                        <AdvancedInfoValue>{formatDate(scan.start_date)}</AdvancedInfoValue>
                      </div>

                      <div>
                        <AdvancedInfoItem>
                          <Layers size={16} />
                          <AdvancedInfoLabel>Scan Type</AdvancedInfoLabel>
                        </AdvancedInfoItem>
                        <StatusBadge
                          bgColor="#f1f5f9"
                          textColor="#3b82f6"
                        >
                          <TypeIcon size={12} />
                          {typeConfig.name}
                        </StatusBadge>
                      </div>
                    </AdvancedInfoGrid>

                    {/* Scan ID */}
                    <ScanIdSection>
                      <ScanIdLabel>Scan ID</ScanIdLabel>
                      <ScanIdWrapper>
                        <ScanIdCode>
                          {scan.form_id}
                        </ScanIdCode>
                        <CopyButton>
                          <Copy size={14} />
                        </CopyButton>
                      </ScanIdWrapper>
                    </ScanIdSection>

                    {/* Actions */}
                    <AdvancedCardFooter>
                      <AdvancedButton
                        disabled={!scan.report_link}
                      >
                        <Eye size={16} />
                        {scan.report_link ? 'View Report' : 'Report Pending'}
                      </AdvancedButton>

                      <AdvancedButton 
                        as={Link}
                        to={`/advanced-scan-report/${scan.scan_type}/${scan.scan_id}`}
                        state={{ organizationId: scan.organization_id }}
                        variant="primary"
                      >
                        <ExternalLink size={16} />
                        View Details
                      </AdvancedButton>
                    </AdvancedCardFooter>

                  </AdvancedCardBody>
                </AdvancedCard>
              );
            })}
          </AdvancedGrid>
        )}
      </Container>
    </div>
  );
}

export default AdvancedReportPage;